/**
 * Performance Monitoring & Error Tracking Service
 * Simple logging service for development and production monitoring
 */

interface ErrorContext {
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
  extra?: Record<string, any>;
  tags?: Record<string, string>;
}

class ErrorTracker {
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    console.log('Error tracking initialized');
    this.initialized = true;
  }

  logError(error: Error, context?: ErrorContext): void {
    console.error('Error logged:', error, context);
  }

  logMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    console.log(`[${level.toUpperCase()}] ${message}`);
  }

  setUser(user: ErrorContext['user']): void {
    console.log('User context set:', user);
  }

  addBreadcrumb(message: string, data?: Record<string, any>): void {
    console.log('Breadcrumb:', message, data);
  }
}

class Analytics {
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    console.log('Analytics initialized');
    this.initialized = true;
  }

  trackPageView(path: string, title?: string): void {
    console.log('Page view:', path, title);
  }

  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    console.log('Event:', eventName, parameters);
  }

  trackFormSubmission(formName: string, success: boolean): void {
    this.trackEvent('form_submission', {
      form_name: formName,
      success: success,
    });
  }

  trackButtonClick(buttonName: string, location?: string): void {
    this.trackEvent('button_click', {
      button_name: buttonName,
      location: location,
    });
  }
}

class PerformanceMonitor {
  logPageLoadTime(): void {
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.timing;
          const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
          const connectTime = perfData.responseEnd - perfData.requestStart;
          const renderTime = perfData.domComplete - perfData.domLoading;

          console.log('Performance Metrics:', {
            pageLoadTime: `${pageLoadTime}ms`,
            connectTime: `${connectTime}ms`,
            renderTime: `${renderTime}ms`,
          });

          analytics.trackEvent('page_performance', {
            load_time: pageLoadTime,
            connect_time: connectTime,
            render_time: renderTime,
          });
        }, 0);
      });
    }
  }

  logWebVitals(): void {
    // Implemented in reportWebVitals.ts
  }
}

export const errorTracker = new ErrorTracker();
export const analytics = new Analytics();
export const performanceMonitor = new PerformanceMonitor();

export const initializeMonitoring = (): void => {
  errorTracker.initialize();
  analytics.initialize();
  performanceMonitor.logPageLoadTime();
  console.log('Monitoring services initialized');
};

export const setupGlobalErrorHandler = (): void => {
  window.addEventListener('error', (event) => {
    errorTracker.logError(new Error(event.message), {
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    errorTracker.logError(
      new Error(`Unhandled Promise Rejection: ${event.reason}`),
      {
        extra: {
          reason: event.reason,
        },
      }
    );
  });
};
